import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import formOptions from "../../config/formOptions.json";

import Header from "../../components/Header";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";
import { READ_OPERATIONAL_AUDIT } from "../../graph/operationalAudits/queries";
import { UPDATE_OPERATIONAL_AUDIT } from "../../graph/operationalAudits/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const auditSchema = yup.object().shape({
  name: yup.string().trim().required("Audit name is required"),
  industry: yup.string().trim().required("Industry is required"),
  status: yup.string().required("Status is required"),
});


const EditOperationalAuditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const { readOperationalAudit } = await client.request(
          READ_OPERATIONAL_AUDIT,
          { id }
        );

        setInitialValues({
          id: readOperationalAudit.id,
          companyId: readOperationalAudit.companyId || "",
          name: readOperationalAudit.name || "",
          industry: readOperationalAudit.industry || "",
          status: readOperationalAudit.status || "DRAFT",
          completedAt: readOperationalAudit.completedAt
            ? readOperationalAudit.completedAt.slice(0, 16)
            : "",
        });
      } catch (err) {
        console.error("Failed to load operational audit:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load operational audit",
        });
      }
    };

    fetchAudit();
  }, [id, setSnackbar]);

  const handleSubmit = async (values) => {
    try {
      const input = {
        id: values.id,
        companyId: values.companyId || null,
        name: values.name,
        industry: values.industry,
        status: values.status,
        completedAt: values.completedAt
          ? new Date(values.completedAt).toISOString()
          : null,
      };

      await client.request(UPDATE_OPERATIONAL_AUDIT, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Operational audit updated successfully",
      });

      navigate("/manageOperationalAudits");
    } catch (err) {
      console.error("Update failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.errors?.[0]?.message ||
          "Failed to update operational audit",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Header
        title="EDIT OPERATIONAL AUDIT"
        subtitle={`Edit ${initialValues.name}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={auditSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Audit Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Company ID"
                name="companyId"
                value={values.companyId}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.industry && !!errors.industry}
              >
                <InputLabel>Industry</InputLabel>
                <Select
                  name="industry"
                  value={values.industry}
                  label="Industry"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {formOptions.industryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.status && !!errors.status}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  label="Status"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {formOptions.StatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Completed At"
                name="completedAt"
                type="datetime-local"
                value={values.completedAt}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate("/manageOperationalAudits")}
              >
                Cancel
              </Button>

              <Button type="submit" variant="contained" color="secondary">
                Save Changes
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditOperationalAuditForm;